import { Node, mergeAttributes } from '@tiptap/core'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { EditorState, Transaction } from '@tiptap/pm/state'

export const JOHKU_COLUMNS_CLASS = 'johku-columns'
export const JOHKU_COLUMN_CLASS = 'johku-column'
export const JOHKU_COLUMNS_COUNT_CLASS = {
  2: 'johku-columns--2',
  3: 'johku-columns--3',
} as const
export const JOHKU_COLUMN_SPAN_2_CLASS = 'johku-column--span-2'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    johkuColumns: {
      insertColumns: (count: 2 | 3) => ReturnType
      mergeColumns: () => ReturnType
      splitColumn: () => ReturnType
      deleteColumns: () => ReturnType
    }
  }
}

type ColumnsContext = {
  columnsPos: number
  columnsNode: ProseMirrorNode
  columnIndex: number
  columnPos: number
  columnNode: ProseMirrorNode
}

function parseColumnsCount(className: string | null): 2 | 3 {
  if (className?.includes(JOHKU_COLUMNS_COUNT_CLASS[2])) {
    return 2
  }
  return 3
}

function parseColumnSpan(className: string | null): 1 | 2 {
  if (className?.includes(JOHKU_COLUMN_SPAN_2_CLASS)) {
    return 2
  }
  return 1
}

function columnsClass(count: 2 | 3): string {
  return `${JOHKU_COLUMNS_CLASS} ${JOHKU_COLUMNS_COUNT_CLASS[count]}`
}

function columnClass(span: 1 | 2): string {
  if (span === 2) {
    return `${JOHKU_COLUMN_CLASS} ${JOHKU_COLUMN_SPAN_2_CLASS}`
  }
  return JOHKU_COLUMN_CLASS
}

function findColumnsContext(state: EditorState): ColumnsContext | null {
  const { $from } = state.selection
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth)
    if (node.type.name !== 'column') {
      continue
    }
    const columnsDepth = depth - 1
    if (columnsDepth < 0) {
      return null
    }
    const columnsNode = $from.node(columnsDepth)
    if (columnsNode.type.name !== 'columns') {
      return null
    }
    return {
      columnsPos: $from.before(columnsDepth),
      columnsNode,
      columnIndex: $from.index(columnsDepth),
      columnPos: $from.before(depth),
      columnNode: node,
    }
  }
  return null
}

function emptyColumn(columnType: ProseMirrorNode['type'], paragraphType: ProseMirrorNode['type'], span: 1 | 2 = 1) {
  return columnType.create({ span }, [paragraphType.create()])
}

function canInsertColumns(state: EditorState): boolean {
  return !findColumnsContext(state)
}

function canMergeColumns(state: EditorState): boolean {
  const ctx = findColumnsContext(state)
  if (!ctx) {
    return false
  }
  if (ctx.columnsNode.attrs.count !== 3 || ctx.columnsNode.childCount !== 3) {
    return false
  }
  if (ctx.columnNode.attrs.span !== 1) {
    return false
  }
  const { columnIndex, columnsNode } = ctx
  const next = columnIndex < columnsNode.childCount - 1 ? columnsNode.child(columnIndex + 1) : null
  if (next && next.attrs.span === 1) {
    return true
  }
  const prev = columnIndex > 0 ? columnsNode.child(columnIndex - 1) : null
  return !!(prev && prev.attrs.span === 1)
}

function canSplitColumn(state: EditorState): boolean {
  const ctx = findColumnsContext(state)
  if (!ctx) {
    return false
  }
  return ctx.columnsNode.attrs.count === 3
    && ctx.columnsNode.childCount === 2
    && ctx.columnNode.attrs.span === 2
}

function canDeleteColumns(state: EditorState): boolean {
  return !!findColumnsContext(state)
}

function mergeColumnsTransaction(state: EditorState, dispatch?: (tr: Transaction) => void): boolean {
  if (!canMergeColumns(state)) {
    return false
  }
  const ctx = findColumnsContext(state)
  if (!ctx) {
    return false
  }

  const { columnsPos, columnsNode, columnIndex } = ctx
  const next = columnIndex < columnsNode.childCount - 1 ? columnsNode.child(columnIndex + 1) : null
  let leftIndex = columnIndex
  let rightIndex = columnIndex + 1
  if (!(next && next.attrs.span === 1)) {
    leftIndex = columnIndex - 1
    rightIndex = columnIndex
  }

  const left = columnsNode.child(leftIndex)
  const right = columnsNode.child(rightIndex)
  const columnType = left.type
  const mergedContent = left.content.append(right.content)
  const merged = columnType.create({ span: 2 }, mergedContent)

  const children: ProseMirrorNode[] = []
  for (let i = 0; i < columnsNode.childCount; i++) {
    if (i === leftIndex) {
      children.push(merged)
      continue
    }
    if (i === rightIndex) {
      continue
    }
    children.push(columnsNode.child(i))
  }

  const nextColumns = columnsNode.type.create({ count: 3 }, children)
  if (dispatch) {
    const tr = state.tr.replaceWith(columnsPos, columnsPos + columnsNode.nodeSize, nextColumns)
    dispatch(tr)
  }
  return true
}

function splitColumnTransaction(state: EditorState, dispatch?: (tr: Transaction) => void): boolean {
  if (!canSplitColumn(state)) {
    return false
  }
  const ctx = findColumnsContext(state)
  if (!ctx) {
    return false
  }

  const { columnsPos, columnsNode, columnIndex, columnNode } = ctx
  const paragraphType = state.schema.nodes.paragraph
  const first = columnNode.type.create({ span: 1 }, columnNode.content)
  const second = emptyColumn(columnNode.type, paragraphType, 1)

  const children: ProseMirrorNode[] = []
  for (let i = 0; i < columnsNode.childCount; i++) {
    if (i === columnIndex) {
      children.push(first, second)
      continue
    }
    children.push(columnsNode.child(i))
  }

  const nextColumns = columnsNode.type.create({ count: 3 }, children)
  if (dispatch) {
    const tr = state.tr.replaceWith(columnsPos, columnsPos + columnsNode.nodeSize, nextColumns)
    dispatch(tr)
  }
  return true
}

export const Column = Node.create({
  name: 'column',
  content: 'block+',
  isolating: true,
  defining: true,

  addAttributes() {
    return {
      span: {
        default: 1,
        parseHTML: (element) => parseColumnSpan(element.getAttribute('class')),
        renderHTML: () => ({}),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `div.${JOHKU_COLUMN_CLASS}`,
        priority: 60,
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const span = (node.attrs.span === 2 ? 2 : 1) as 1 | 2
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: columnClass(span) }),
      0,
    ]
  },
})

export const Columns = Node.create({
  name: 'columns',
  group: 'block',
  content: 'column{2,3}',
  isolating: true,
  defining: true,

  addAttributes() {
    return {
      count: {
        default: 2,
        parseHTML: (element) => parseColumnsCount(element.getAttribute('class')),
        renderHTML: () => ({}),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `div.${JOHKU_COLUMNS_CLASS}`,
        priority: 60,
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const count = (node.attrs.count === 3 ? 3 : 2) as 2 | 3
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: columnsClass(count) }),
      0,
    ]
  },

  addCommands() {
    return {
      insertColumns: (count: 2 | 3) => ({ state, dispatch, commands }) => {
        if (!canInsertColumns(state)) {
          return false
        }
        if (!state.schema.nodes.column || !state.schema.nodes.columns || !state.schema.nodes.paragraph) {
          return false
        }
        if (!dispatch) {
          return true
        }
        return commands.insertContent({
          type: 'columns',
          attrs: { count },
          content: Array.from({ length: count }, () => ({
            type: 'column',
            attrs: { span: 1 },
            content: [{ type: 'paragraph' }],
          })),
        })
      },

      mergeColumns: () => ({ state, dispatch }) => mergeColumnsTransaction(state, dispatch),

      splitColumn: () => ({ state, dispatch }) => splitColumnTransaction(state, dispatch),

      deleteColumns: () => ({ state, dispatch }) => {
        if (!canDeleteColumns(state)) {
          return false
        }
        const ctx = findColumnsContext(state)
        if (!ctx) {
          return false
        }
        if (dispatch) {
          const tr = state.tr.delete(ctx.columnsPos, ctx.columnsPos + ctx.columnsNode.nodeSize)
          dispatch(tr)
        }
        return true
      },
    }
  },
})

export function canRunInsertColumns(editor: { state: EditorState }): boolean {
  return canInsertColumns(editor.state)
}

export function canRunMergeColumns(editor: { state: EditorState }): boolean {
  return canMergeColumns(editor.state)
}

export function canRunSplitColumn(editor: { state: EditorState }): boolean {
  return canSplitColumn(editor.state)
}

export function canRunDeleteColumns(editor: { state: EditorState }): boolean {
  return canDeleteColumns(editor.state)
}

export default Columns
